import {
  createPrintError,
  PRINT_ERROR_CODES,
} from "./contracts";

const ESC = 0x1b;
const GS = 0x1d;

function toBytes(value) {
  if (value instanceof Uint8Array) {
    return value;
  }

  if (value instanceof ArrayBuffer) {
    return new Uint8Array(value);
  }

  if (Array.isArray(value)) {
    return new Uint8Array(value);
  }

  return new TextEncoder().encode(String(value ?? ""));
}

function concatBytes(...chunks) {
  const arrays = chunks.map(toBytes);
  const total = arrays.reduce((sum, item) => sum + item.length, 0);
  const result = new Uint8Array(total);

  let offset = 0;

  arrays.forEach((item) => {
    result.set(item, offset);
    offset += item.length;
  });

  return result;
}

function textBytes(text) {
  return new TextEncoder().encode(String(text ?? ""));
}

/**
 * Low-level ESC/POS builder.
 *
 * This is intentionally transport-agnostic:
 * it creates bytes only and does not know whether
 * the bytes will be sent through QZ, Android Bridge,
 * TCP, USB, or another transport.
 */
export function createEscPosBuilder() {
  const chunks = [];

  const api = {
    initialize() {
      chunks.push(Uint8Array.from([ESC, 0x40]));
      return api;
    },

    align(value = "left") {
      const map = {
        left: 0,
        center: 1,
        right: 2,
      };

      chunks.push(
        Uint8Array.from([
          ESC,
          0x61,
          map[value] ?? map.left,
        ])
      );

      return api;
    },

    bold(enabled = true) {
      chunks.push(
        Uint8Array.from([
          ESC,
          0x45,
          enabled ? 1 : 0,
        ])
      );

      return api;
    },

    size(value = "normal") {
      const map = {
        normal: 0x00,
        double_width: 0x10,
        double_height: 0x01,
        double: 0x11,
      };

      chunks.push(
        Uint8Array.from([
          GS,
          0x21,
          map[value] ?? map.normal,
        ])
      );

      return api;
    },

    text(value = "") {
      chunks.push(textBytes(value));
      return api;
    },

    line(value = "") {
      chunks.push(textBytes(`${value}\n`));
      return api;
    },

    feed(lines = 1) {
      chunks.push(
        Uint8Array.from([
          ESC,
          0x64,
          Math.max(0, Math.min(255, Number(lines) || 0)),
        ])
      );

      return api;
    },

    cut() {
      chunks.push(
        Uint8Array.from([
          GS,
          0x56,
          0x00,
        ])
      );

      return api;
    },

    raw(value) {
      chunks.push(toBytes(value));
      return api;
    },

    build() {
      return concatBytes(...chunks);
    },
  };

  return api;
}

/**
 * Generic ESC/POS renderer contract.
 *
 * Supported input:
 *   document.payload -> raw ESC/POS bytes
 *
 * Or:
 *   document.lines -> simple text lines.
 *
 * Receipt-specific formatting can be expanded later
 * without coupling this renderer to a transport.
 */
export function renderEscPos(document = {}, printer = {}) {
  if (document?.payload) {
    return toBytes(document.payload);
  }

  if (Array.isArray(document?.lines)) {
    const builder = createEscPosBuilder();

    builder.initialize();

    document.lines.forEach((line) => {
      if (typeof line === "string") {
        builder.line(line);
        return;
      }

      if (!line || typeof line !== "object") {
        return;
      }

      if (line.align) {
        builder.align(line.align);
      }

      if (typeof line.bold === "boolean") {
        builder.bold(line.bold);
      }

      if (line.size) {
        builder.size(line.size);
      }

      if (line.text !== undefined) {
        builder.line(line.text);
      }
    });

    builder.feed(3);

    if (printer?.capabilities?.cut) {
      builder.cut();
    }

    return builder.build();
  }

  throw createPrintError(
    PRINT_ERROR_CODES.PRINT_JOB_REJECTED,
    "Dokumen belum memiliki payload atau struktur ESC/POS yang dapat dirender."
  );
}
